import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { WalletsDialog } from '@root/widgets/desktop/wallets-dialog'

import { NodeBase } from './common/nodes-common'
import { NodeBalanceOutlet } from './node-amount-outlet'
import { NodeRefreshButton } from './node-refresh-button'
import { PlaceholderNode } from './placeholder-node'

import type { DistributeFundsModel } from '../../atoms'
import type { WalletModel } from '@root/entities/wallet'
import type { StyleProps } from '@root/shared/react'

type Props = {
  model: DistributeFundsModel
  ref?: React.Ref<HTMLDivElement>
} & StyleProps

export const WalletSourceNode: React.FC<Props> = ({ model, style, ref }) => {
  const [sourceWallet, setSourceWallet] = useAtom(model.sourceWallet)
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const node = (() => {
    if (sourceWallet) {
      return (
        <WalletSourceNodeReady
          style={style}
          ref={ref}
          wallet={sourceWallet}
          onOpenWalletsSelection={() => {
            setOpen(true)
          }}
        />
      )
    }

    return (
      <>
        <PlaceholderNode
          style={style}
          ref={ref}
          data-node-type="source-wallet"
          label={t('wallet')}
          onClick={() => {
            setOpen(true)
          }}
        />
      </>
    )
  })()

  return (
    <>
      {node}
      <WalletsDialog
        open={open}
        onOpenChange={setOpen}
        selectedWalletId={undefined}
        onWalletSelected={(wallet) => {
          setSourceWallet(wallet)
          setOpen(false)
        }}
      />
    </>
  )
}

const WalletSourceNodeReady = ({
  onOpenWalletsSelection,
  wallet,
  style,
  ref,
}: {
  wallet: WalletModel
  onOpenWalletsSelection: () => void
  ref?: React.Ref<HTMLDivElement>
} & StyleProps) => {
  const [balance] = useAtom(wallet.totalBalanceNative)
  const [balanceUsd] = useAtom(wallet.totalBalanceUsd)
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  return (
    <Root style={style} data-node-type="source-wallet" ref={ref}>
      <Header>
        <ShortStyled size="xs" wallet={wallet} />
        <NodeRefreshButton onClick={onOpenWalletsSelection} />
      </Header>
      <NodeBalanceOutlet
        balanceCurrency={balance}
        balanceUsd={balanceUsd}
        currency={nativeCurrency}
      />
    </Root>
  )
}

const Root = styled(NodeBase)`
  padding-inline: 3px;
  padding-block: 3px;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  height: 65px;
  width: 110px;
`

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  justify-content: space-between;
  padding-inline: 5px;
`

const ShortStyled = styled(WalletModelShort)`
  --wallet-short-indicator-size: 3px;
  font-size: 11px;
  font-weight: 600;
`
